<template>
  <Dropdown
    data-test="search-bar"
    ref="dropdown"
    menu-right
    tag="li"
    v-model="show"
    class="search-bar dropdown-form"
    :class="{ 'has-results': model || hasError, 'hidden': isTabletOrMobile && sideNavIsOpen }"
  >
    <a
      href="javascript: void(0);"
      role="button"
      class="dropdown-toggle"
      id="search-button"
      aria-label="Search button"
    >
      <span class="search-button glyphicon glyphicon-search"></span>
    </a>
    <input
      v-if="show"
      v-model="query"
      ref="searchbox"
      class="search-box form-control input-lg"
      type="search"
      placeholder="Search"
      aria-label="Search"
    />

    <!--    eslint-disable-next-line -->
    <template #dropdown class="search-results">
      <li
        class="search-result"
        v-for="(result, index) in model && model.results"
        v-bind:key="result.cmsUid"
      >
        <a
          v-if="result.type === 'Prismic'"
          role="button"
          @click="handleClick(result, index + 1, $event, true)"
          @contextmenu="handleClick(result, index + 1, $event, false)"
          :href="getUrlForResult(result)"
        >
          <img
            class="search-result-tile"
            :src="result.imageUrl ?? undefined"
            :style="{ visibility: result.imageUrl ? 'visible' : 'hidden' }"
            alt="Result image"
          />
          <span
            class="search-result-type label label-default"
            :class="['search-result-type-' + result.cmsType]"
            >{{ getResourceTypeName(result) }}</span
          >
          <h4>{{ result.title }}</h4>
          <div class="search-result-highlights">
            <p v-for="highlight in result.highlights" v-bind:key="highlight" v-html="highlight"></p>
          </div>
        </a>
      </li>
      <li v-if="model && model.results && !model.results.length">
        <h4 class="text-center">No matching results.</h4>
      </li>
      <li v-if="hasError">
        <h4 class="text-center">There was a problem retrieving search results.</h4>
      </li>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { isMobile } from "mobile-device-detect";
import { debug as d } from "debug";
import axios from "axios";
import { Dropdown } from "uiv";
import { useRouter } from "vue-router";
import cms from "../services/cms/cms";
import { CmsResourceLink } from "../services/cms/cms-service";
import { CmsDocumentTypes } from "../services/cms/cms-type-definitions";
import { SearchClickApiModel, SearchResultApiModel, SearchResultsApiModel } from "../api-models";

const debug = d("cs.SearchBar");

const isTabletOrMobile = ref(isMobile);
const show = ref(false);
const query = ref("");
const model = ref<SearchResultsApiModel | null>(null);
const serverCallTimeout = ref<any>(null);
const isLoading = ref(false);
const hasError = ref(false);

const props = defineProps<{ sideNavIsOpen: boolean }>();

const router = useRouter();

// template refs
const searchbox = ref<HTMLInputElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);

function performSearch(): void {
  const sanitizedQuery = (query.value ?? "").trim().replace(/[^\w* ]/g, "");
  if (sanitizedQuery.length < 3) {
    model.value = null;
    return;
  }

  // Simple debounce to avoid hammering the server
  if (serverCallTimeout.value) {
    clearTimeout(serverCallTimeout.value);
  }
  serverCallTimeout.value = setTimeout(() => {
    debug("Searching", sanitizedQuery);
    isLoading.value = true;
    hasError.value = false;
    serverCallTimeout.value = null;
    axios
      .get("/api/search", {
        params: {
          query: sanitizedQuery,
        },
      })
      .then((resp): void => {
        const data = resp.data as SearchResultsApiModel;
        model.value = data;
        debug(`Got results for: ${sanitizedQuery}`);
        isLoading.value = false;
      })
      .catch((err) => {
        debug(`Got an error for: ${sanitizedQuery}`, err);
        isLoading.value = false;
        hasError.value = true;
        model.value = null;
      });
  }, 500);
}

watch(show, (newValue: boolean, oldValue: boolean) => {
  if (!oldValue && newValue) {
    setTimeout(() => {
      (searchbox.value as HTMLInputElement).focus();
    }, 100);
  } else {
    query.value = "";
    model.value = null;
    hasError.value = false;
    isLoading.value = false;
    serverCallTimeout.value = null;
  }
});

watch(query, () => {
  performSearch();
});

function getUrlForResult(result: SearchResultApiModel): string | undefined {
  if (result.type === "Prismic" && !!result.cmsType && result.cmsUid) {
    return (
      result.urlOverride ??
      cms.linkResolver({
        id: result.cmsUid,
        type: result.cmsType,
      } as CmsResourceLink)
    );
  }
  return result.urlOverride ?? undefined;
}

function getResourceTypeName(result: SearchResultApiModel): string {
  switch (result.cmsType as CmsDocumentTypes) {
    case CmsDocumentTypes.CmsResource:
      return "Resource";
    case CmsDocumentTypes.CmsLeaderType:
      return "Leader type";
    case CmsDocumentTypes.CmsLinkResource:
      return result.typeNameOverride || "Resource";
    case CmsDocumentTypes.CmsStrengthItem:
      return "Strength";
    case CmsDocumentTypes.CmsSession:
    case CmsDocumentTypes.CmsSessionWithSlides:
      return "Session";
    case CmsDocumentTypes.CmsWorkshopPage:
      return "Step";
    case CmsDocumentTypes.CmsResourcePlaylist:
      return "Playlist";
    case CmsDocumentTypes.CmsResourceTheme:
      return "Theme";
    case CmsDocumentTypes.CmsSessionWheel:
      return "Wheel";
    case CmsDocumentTypes.CmsTeamPathway:
      return "Team Pathway";
    case CmsDocumentTypes.CmsPersonalPathway:
      return "Personal Pathway";
    default:
      return "Other";
  }
}

function handleClick(
  result: SearchResultApiModel,
  rank: number,
  event: Event,
  navigateAndPreventDefault: boolean,
): void {
  const url = getUrlForResult(result);

  if (!!url && !!model.value) {
    if (navigateAndPreventDefault) {
      event.preventDefault();
    }
    axios
      .post("/api/search", {
        searchCorrelationId: model.value.searchCorrelationId,
        resultRank: rank,
        result,
      } as SearchClickApiModel)
      .finally(() => {
        if (navigateAndPreventDefault) {
          window.location.href = url;
        }
      });
  }
}

function getClosest(element: HTMLElement | null, className: string): HTMLElement | null {
  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    if (currentElement.classList && currentElement.classList.contains(className)) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }
  return null;
}

function fixSafariLayoutBug(): void {
  // Fix an issue where dynamic insertion of the Vue dropdown into the nav
  // within a Razor view leads to incorrect navbars in Safari. This seems to be because
  // navbars is determined on the <component> element included before JS has run.
  // Just removing and re-adding the nav's classes fixes the problem.
  if (!dropdown.value) {
    return;
  }
  const nav = getClosest(dropdown.value, "navbar");
  if (nav) {
    const existingNavbarClasses = Array.from(nav.classList);
    existingNavbarClasses.forEach((c) => nav.classList.remove(c));
    nav.classList.add("hidden");

    setTimeout(() => {
      existingNavbarClasses.forEach((c) => nav.classList.add(c));
      nav.classList.remove("hidden");
    }, 0);
  }
}
onMounted(() => {
  fixSafariLayoutBug();
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../styles/bootstrap/variables.less";
@search-result-width: 700px;

.search-box {
  position: absolute;
  z-index: 9999;
  top: 2px;
  right: 100%;
  width: 300px;
  max-width: 300px;
}

#search-button {
  padding: 15px;
  margin-right: 15px;
  &:focus {
    outline-offset: 5px;
    outline: black auto;
  }
}

.stop-clicks {
  pointer-events: none;
}

.search-bar .dropdown-menu {
  width: 700px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  visibility: hidden;
}

.search-bar.has-results .dropdown-menu {
  visibility: visible;
}

.search-results {
  width: @search-result-width;
  max-width: 80vw;
}

.search-result {
  border-bottom: 1px solid lightgray;
}

.search-result a {
  min-height: 90px;
}

.search-result a:focus,
.search-result a:hover {
  background-color: #eeeeee !important;
}

.search-result a > h4 {
  margin-right: 100px;
  white-space: normal;
}

.search-result-highlights {
  margin-bottom: 10px;

  p {
    margin: 0 20px 0 95px;
    white-space: normal;
    max-height: 3em;
    overflow: hidden;
    font-style: italic;
    font-size: 80%;
  }
}

.search-result-type {
  float: right;
  margin-top: 10px;
}

.search-result-type-cms-resource {
  background-color: coral;
}

.search-result-type-cms-workshop-page {
  background-color: blueviolet;
}

.search-result-type-cms-session-with-slides {
  background-color: orange;
}

.search-result-type-cms-resource-playlist {
  background-color: green;
}

.search-result-type-cms-pathway {
  background-color: #17a2b8;
}

.search-result-type-cms-personal-pathway {
  background-color: #17a2b8;
}

img.search-result-tile {
  float: left;
  margin-right: 15px;
  margin-top: 2px;
  width: 80px;
  height: 80px;
  border: none;
  border-image-width: 0;
}

@search-bar-dropdown-width: @search-result-width + @side-nav-collapsed-width;

@media (max-width: @search-bar-dropdown-width) {
  .search-bar .dropdown-menu {
    width: calc(100vw - @side-nav-collapsed-width);
  }

  .search-box {
    width: calc(100vw - @side-nav-collapsed-width * 2);
    max-width: none;
  }
}

@media (max-width: @screen-xxs-max) {
  .search-box {
    width: calc(100vw - @side-nav-collapsed-width);
  }

  .search-bar .dropdown-menu {
    width: 100vw;
  }
}
</style>
